/**
 * InterestPaymentsStep Component
 * Step 2: Interest rate, day count basis, and payment frequency settings
 */

import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import LabeledSwitch, { InlineGateToggle } from '../../../common/LabeledSwitch';
import ToggleGroup from '../../../common/ToggleGroup';
import { RATE_CHANGE_BASIS } from '../../../../utils/formatters';

function InterestPaymentsStep() {
  const { control, watch, setValue } = useFormContext();
  const {
    refData,
    selectedBenchmarkRate,
    setSelectedBenchmarkRate,
    hasSchedule,
    setHasSchedule,
    setSchedulePreview,
  } = useContractFormContext();

  // T077: confirm before swapping the calculation profile when a schedule
  // already exists. The SP-side guard clears the schedule on save, but the
  // user deserves a heads-up first so they're not surprised when the
  // schedule preview empties on the next regenerate.
  const [pendingProfileChange, setPendingProfileChange] = React.useState(null);

  const rateType = watch('rate_type');
  const rateSpread = watch('rate_spread') || 0;
  // Both 'Variable' (displayed as "Linked") and 'Floating' carry a benchmark rate
  // + spread that drive the effective interest rate. Use this for every field that
  // is benchmark-driven. (Variable-only fields — effective-basis, update-mode, the
  // ad-hoc rate-change workflow — stay gated on rateType === 'Variable'.)
  const usesBenchmark = rateType === 'Variable' || rateType === 'Floating';
  const watchedBenchmarkRateId = watch('benchmark_rate_id');
  const selectedCountryId = watch('investment_currency_id');
  const selectedProfileId = watch('calculation_profile_id');
  const eligibleForRepricing = watch('eligible_for_repricing');
  const eligibleForRenewal = watch('eligible_for_renewal');
  // T3-068: 'specified_date' basis reveals a frequency + first-date pair (Variable only).
  const rateChangeBasis = watch('rate_change_effective_basis');
  // T3-098: custom rate-change cadence (months|years + value). The gate is DERIVED from the
  // unit rather than held as its own boolean, so a contract loaded for edit shows the toggle
  // already on without a sync effect, and there is no third piece of state to drift.
  const rateChangeCustomUnit = watch('rate_change_custom_unit');
  const rateChangeCustomValue = watch('rate_change_custom_value');
  const rateChangeCustomOn = rateChangeCustomUnit === 'months' || rateChangeCustomUnit === 'years';
  const investmentTerm = watch('investment_term');
  const investmentTermValue = watch('investment_term_value');
  const depositDate = watch('deposit_date');
  const maturityDate = watch('maturity_date');
  const interestFreqId = watch('interest_paid_frequency_id');
  const capitalFreqId = watch('capital_paid_frequency_id');
  const combinePaymentRows = watch('combine_payment_rows');
  // "At Maturity" interest = the sentinel frequency row (months_value === 999): interest
  // accrues across the whole term and is paid as a single lump at maturity. There is no
  // periodic interest stream, so Combined-payment and Equal-installment are meaningless
  // and are force-cleared + disabled below (mirrors the Compound/SPC profile forcing).
  const isInterestAtMaturity = (refData.frequencies || []).some(
    (f) => f.id === interestFreqId && f.months_value === 999
  );
  const equalInstallment = watch('equal_installment');

  // Compute contract term in months for re-pricing frequency validation
  const contractTermMonths = (() => {
    const value = parseInt(investmentTermValue, 10);
    if (!value || Number.isNaN(value)) return null;
    if (investmentTerm === 'Years') return value * 12;
    if (investmentTerm === 'Months') return value;
    if (investmentTerm === 'Days') return value / 30.44;
    return null;
  })();

  // T3-098: echo the resolved cadence back to the user, so "3" + "Years" reads as the 36 months
  // the engine will actually use. Mirrors the server-side resolver
  // (fn_contract_rate_change_months) but is DISPLAY ONLY — the contract is never saved from this.
  const rateChangeCadenceHint = (() => {
    const n = parseInt(rateChangeCustomValue, 10);
    if (!rateChangeCustomOn || !n || Number.isNaN(n) || n <= 0) return 'Whole numbers only';
    const months = n * (rateChangeCustomUnit === 'years' ? 12 : 1);
    const unitLabel = rateChangeCustomUnit === 'years' ? (n === 1 ? 'year' : 'years') : (n === 1 ? 'month' : 'months');
    return rateChangeCustomUnit === 'years'
      ? `Rate may change every ${n} ${unitLabel} (${months} months)`
      : `Rate may change every ${n} ${unitLabel}`;
  })();

  // Filter out Adhoc (no fixed cadence) and any frequency longer than the contract term
  const repricingFrequencyOptions = (refData.frequencies || []).filter((f) => {
    if (!f || typeof f.frequency !== 'string') return false;
    if (f.frequency.toLowerCase() === 'adhoc') return false;
    if (contractTermMonths != null && f.months_value && f.months_value > contractTermMonths) return false;
    return true;
  });

  // Get the currently selected profile for display
  const selectedProfile = selectedProfileId
    ? (refData.calculationProfiles || []).find(p => p.id === parseInt(selectedProfileId))
    : null;

  // Handle profile selection — auto-fill interest calc type and day count basis
  const handleProfileChange = (profileId) => {
    setValue('calculation_profile_id', profileId || null);
    if (profileId) {
      const profile = (refData.calculationProfiles || []).find(p => p.id === parseInt(profileId));
      if (profile) {
        // Auto-fill day count basis
        setValue('day_count_basis', String(profile.day_count_basis));
        // Auto-set capitalization frequency for SPC profiles
        if (profile.interest_method === 'Simple Periodic Capitalization') {
          const annualFreq = (refData.frequencies || []).find(f => f.frequency === 'Annually' || f.frequency === 'Annual');
          if (annualFreq) {
            setValue('capitalization_frequency_id', annualFreq.id);
          }
        } else {
          setValue('capitalization_frequency_id', '');
        }
        // T076: Annuity profiles require equal_installment + combine_payment_rows.
        // Force both on; the toggles render as read-only captions for Annuity.
        // For Simple, leave equal_installment alone (user choice). For everything
        // else (Compound, Compound Linear, SPC) clear equal_installment because
        // it's only supported on Simple + Annuity at the engine level.
        if (profile.interest_method === 'Annuity') {
          // Capital frequency MUST be back-filled here, not only in the interest-frequency
          // onChange below. That handler syncs capital only `if (combinePaymentRows)` — i.e.
          // when the flag is ALREADY on — so the two orderings diverged:
          //   profile -> interest freq : handler fires, capital syncs        (dev c378, freq 13)
          //   interest freq -> profile : this branch runs, capital stays NULL (dev c379, NULL)
          // The engine's equal-installment gate requires interest === capital frequency, so the
          // NULL case silently produced an interest-only schedule with no capital instalments —
          // while the disabled field claimed "Locked to interest payment frequency".
          //
          // At Maturity (999) and Adhoc (998) are sentinels the engine's EI gate also rejects, so
          // copying one across would swap one broken pair for another. Mirror the onChange below:
          // on a non-periodic interest cadence, leave the flags OFF rather than fabricate an
          // invalid combination.
          const interestFreq = (refData.frequencies || []).find(
            (f) => f.id === parseInt(interestFreqId, 10)
          );
          const interestIsPeriodic = interestFreq
            && interestFreq.months_value != null
            && interestFreq.months_value > 0
            && interestFreq.months_value < 998;

          if (interestIsPeriodic) {
            setValue('equal_installment', true);
            setValue('combine_payment_rows', true);
            setValue('capital_paid_frequency_id', interestFreq.id);
          } else {
            // No periodic interest stream to combine with — the engine would reject the pair.
            setValue('equal_installment', false);
            setValue('combine_payment_rows', false);
          }
        } else if (profile.interest_method !== 'Simple') {
          setValue('equal_installment', false);
        }
      }
    }
  };

  // Filter to only active rates for the selected country and group by type
  const activeRates = refData.interestRates.filter(rate =>
    rate.active !== false &&
    (!selectedCountryId || rate.country_id === parseInt(selectedCountryId))
  );

  const groupedRates = activeRates.reduce((acc, rate) => {
    const type = rate.interest_rate_type || rate.rate_type || 'Other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(rate);
    return acc;
  }, {});

  // Get the latest rate for each type
  const latestRatesByType = Object.entries(groupedRates).map(([type, rates]) => {
    const sorted = [...rates].sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
    return sorted[0];
  }).filter(Boolean);

  // Ensure the saved benchmark rate is in the options (it may be an older rate, not the latest)
  if (watchedBenchmarkRateId) {
    const savedRate = refData.interestRates.find(r => r.id === parseInt(watchedBenchmarkRateId));
    if (savedRate && !latestRatesByType.find(r => r.id === savedRate.id)) {
      latestRatesByType.push(savedRate);
    }
  }

  // Benchmark availability for the selected country. latestRatesByType is empty only
  // when the country has no active rate AND the contract isn't already tied to a saved
  // benchmark (the grandfather clause is handled by the saved-rate push above). This
  // drives both the rate_type guard and the explanatory message below, so a
  // Linked/Floating rate type can never be chosen with no benchmark to point at.
  const hasBenchmarkForCountry = latestRatesByType.length > 0;

  // Get benchmark rate details for benchmark-linked contracts (Linked/Variable + Floating)
  const selectedBenchmark = watchedBenchmarkRateId
    ? refData.interestRates.find(r => r.id === parseInt(watchedBenchmarkRateId))
    : null;
  const benchmarkRateValue = selectedBenchmark
    ? parseFloat(selectedBenchmark.effective_interest_rate || selectedBenchmark.rate || 0)
    : 0;
  const effectiveRate = benchmarkRateValue + parseFloat(rateSpread || 0);

  // Handle benchmark rate selection (Linked/Variable + Floating contracts)
  const handleBenchmarkRateChange = (event) => {
    const selectedId = event.target.value;
    setSelectedBenchmarkRate(selectedId);
    if (selectedId) {
      const rate = refData.interestRates.find(r => r.id === parseInt(selectedId));
      if (rate) {
        const rateValue = rate.effective_interest_rate || rate.rate;
        setValue('benchmark_rate_id', rate.id);
        // Calculate effective rate with spread
        const spread = parseFloat(rateSpread) || 0;
        setValue('interest_rate', parseFloat(rateValue) + spread);
      }
    } else {
      setValue('benchmark_rate_id', null);
    }
  };

  // Handle spread change for benchmark-linked contracts (Linked/Variable + Floating)
  const handleSpreadChange = (newSpread) => {
    setValue('rate_spread', newSpread);
    if (usesBenchmark && benchmarkRateValue > 0) {
      setValue('interest_rate', benchmarkRateValue + parseFloat(newSpread || 0));
    }
  };

  // Keep interest_rate in sync with benchmark + spread whenever a benchmark is
  // selected (covers edit-mode load and benchmark/spread changes), so the stored
  // rate can never drift from the displayed Effective Rate. The field is read-only
  // while a benchmark is selected; this effect is the single source of truth for it.
  useEffect(() => {
    if (selectedBenchmark) {
      const eff = benchmarkRateValue + parseFloat(rateSpread || 0);
      if (parseFloat(watch('interest_rate')) !== eff) {
        setValue('interest_rate', eff);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBenchmark, benchmarkRateValue, rateSpread]);

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Interest Settings
          </Typography>

          <Grid container spacing={3}>
            {/* Rate Type */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="rate_type"
                control={control}
                rules={{
                  validate: (value) => {
                    if ((value === 'Variable' || value === 'Floating') && !hasBenchmarkForCountry) {
                      return 'No benchmark rate exists for the selected country. Add an interest rate for this country in Reference Data, or choose a Fixed rate.';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    error={!!error}
                    helperText={error ? error.message : ''}
                    label="Rate Type *"
                    fullWidth
                    onChange={(e) => {
                      const newType = e.target.value;
                      field.onChange(e);
                      // Clear benchmark/spread when switching to a non-benchmark type
                      // (Fixed). Both Linked/Variable and Floating keep them.
                      if (newType !== 'Variable' && newType !== 'Floating') {
                        setValue('rate_spread', 0);
                        setValue('benchmark_rate_id', null);
                        setValue('rate_change_effective_basis', 'immediate');
                        setSelectedBenchmarkRate('');
                      }
                      // Effective-basis / update-mode are Variable-only — reset them
                      // when leaving Variable (incl. switching to Floating). T3-068: the
                      // specified_date cadence fields go with it.
                      if (newType !== 'Variable') {
                        setValue('rate_change_effective_basis', 'immediate');
                        setValue('rate_change_frequency_id', '');
                        setValue('first_rate_change_date', '');
                      }
                      // Clear repricing + schedule-editing (Floating-only) fields
                      // when switching away from Floating.
                      if (newType !== 'Floating') {
                        setValue('eligible_for_repricing', false);
                        setValue('repricing_frequency_id', '');
                        setValue('first_repricing_date', '');
                        setValue('allow_schedule_editing', false);
                      }
                    }}
                  >
                    <MenuItem value="Fixed">Fixed</MenuItem>
                    {/* Stored value stays 'Variable'; label shown to users is 'Linked'. */}
                    <MenuItem value="Variable">Linked</MenuItem>
                    <MenuItem value="Floating">Floating</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {/* Benchmark Rate - Linked/Variable + Floating contracts */}
            {usesBenchmark && latestRatesByType.length > 0 && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="benchmark_rate_id"
                  control={control}
                  rules={{ required: usesBenchmark ? 'Benchmark rate is required for this rate type' : false }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error}>
                      <InputLabel id="benchmark-rate-label">
                        Benchmark Rate {usesBenchmark ? '*' : ''}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="benchmark-rate-label"
                        label={`Benchmark Rate ${usesBenchmark ? '*' : ''}`}
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleBenchmarkRateChange(e);
                        }}
                      >
                        <MenuItem value="">
                          <em>Select benchmark rate...</em>
                        </MenuItem>
                        {latestRatesByType.map((rate) => {
                          const benchmarkType = rate.interest_rate_type || rate.rate_type;
                          const benchmarkValue = rate.effective_interest_rate || rate.rate;
                          return (
                            <MenuItem key={rate.id} value={rate.id}>
                              {benchmarkType}: {benchmarkValue}%
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                    </FormControl>
                  )}
                />
                <Typography variant="caption" color="text.secondary">
                  The benchmark rate this contract follows (e.g., Prime, JIBAR)
                </Typography>
              </Grid>
            )}

            {/* No benchmark rate for the selected country: explain the dead-end instead of
                silently hiding the selector (a Linked/Floating type needs a benchmark). */}
            {usesBenchmark && !hasBenchmarkForCountry && (
              <Grid
                size={{
                  xs: 12,
                  md: 8
                }}>
                <Alert severity="warning">
                  No benchmark rate exists for the selected country. Add an interest rate for this
                  country under Reference Data &rarr; Interest Rates, or choose a Fixed rate type.
                </Alert>
              </Grid>
            )}

            {/* Rate Spread - for benchmark-linked contracts (Linked/Variable + Floating) */}
            {usesBenchmark && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_spread"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rate Spread (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      value={field.value || 0}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        handleSpreadChange(e.target.value);
                      }}
                      helperText="+ or - adjustment from benchmark (e.g., Prime + 2% = spread of 2)"
                    />
                  )}
                />
              </Grid>
            )}

            {/* Rate Change Effective Basis - Variable contracts only */}
            {rateType === 'Variable' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_change_effective_basis"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Rate Change Effective Basis"
                      fullWidth
                      value={field.value || RATE_CHANGE_BASIS.IMMEDIATE}
                      onChange={(e) => {
                        field.onChange(e);
                        // T3-068: the cadence fields belong only to the specified_date basis —
                        // clear them when switching to any other basis (mirrors the repricing/renewal
                        // toggle clears) so a stale frequency/date can't ride along on save.
                        if (e.target.value !== RATE_CHANGE_BASIS.SPECIFIED_DATE) {
                          setValue('rate_change_frequency_id', '');
                          setValue('first_rate_change_date', '');
                          // T3-098: the custom cadence belongs to this basis too — clear it or a
                          // stale unit/value would keep driving the rate timeline. The DB CHECK
                          // chk_rate_change_custom_requires_specified_basis also refuses it.
                          setValue('rate_change_custom_unit', '');
                          setValue('rate_change_custom_value', '');
                        }
                      }}
                      helperText="When benchmark rate changes take effect on this contract"
                    >
                      <MenuItem value={RATE_CHANGE_BASIS.IMMEDIATE}>On Date of Change</MenuItem>
                      <MenuItem value={RATE_CHANGE_BASIS.ANNIVERSARY}>On Annual Anniversary</MenuItem>
                      <MenuItem value={RATE_CHANGE_BASIS.SPECIFIED_DATE}>On Specified Date</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {/* T3-068: On Specified Date basis reveals a frequency + first effective date (Variable
                only), mirroring the Re-pricing / Renewal "frequency + first date" pattern. A benchmark
                rate change then shifts to the next date in this series on/after the change. */}
            {rateType === 'Variable' && rateChangeBasis === RATE_CHANGE_BASIS.SPECIFIED_DATE && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_change_frequency_id"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (rateChangeBasis !== RATE_CHANGE_BASIS.SPECIFIED_DATE) return true;
                      // T3-098: a CUSTOM interval REPLACES this field — the engine resolves the
                      // cadence from the unit/value pair (fn_contract_rate_change_months) and never
                      // reads the frequency's months_value. Requiring a value the system then
                      // ignores is a dead field, so the whole rule stands down when Custom is on.
                      if (rateChangeCustomOn) return true;
                      if (!value) return 'Rate change frequency is required';
                      const selected = (refData.frequencies || []).find((f) => f.id === parseInt(value, 10));
                      if (selected?.frequency?.toLowerCase() === 'adhoc') return 'Adhoc cannot be used for a specified rate-change date';
                      if (selected && selected.months_value >= 998) return 'At Maturity cannot be used for a specified rate-change date';
                      if (contractTermMonths != null && selected?.months_value > contractTermMonths) return 'Rate change frequency cannot exceed the contract term';
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      // T3-098: not required — and not read — while a custom interval is on, so the
                      // asterisk goes and the control is disabled rather than sitting there looking
                      // mandatory. Greyed-out-but-visible (not hidden) keeps the layout stable and
                      // shows the user what the custom interval is standing in for.
                      label={rateChangeCustomOn ? 'Rate Change Frequency' : 'Rate Change Frequency *'}
                      fullWidth
                      disabled={rateChangeCustomOn}
                      value={rateChangeCustomOn ? '' : (field.value || '')}
                      error={!!error}
                      helperText={error?.message || (rateChangeCustomOn
                        ? 'Replaced by the custom interval'
                        : 'How often a benchmark rate change may take effect')}
                    >
                      <MenuItem value=""><em>Select frequency...</em></MenuItem>
                      {repricingFrequencyOptions.map((freq) => (
                        <MenuItem key={freq.id} value={freq.id}>{freq.frequency}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {rateType === 'Variable' && rateChangeBasis === RATE_CHANGE_BASIS.SPECIFIED_DATE && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="first_rate_change_date"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (rateChangeBasis !== RATE_CHANGE_BASIS.SPECIFIED_DATE) return true;
                      if (!value) return 'First rate change date is required';
                      if (depositDate && value <= depositDate) return 'Must be after the deposit date';
                      if (maturityDate && value >= maturityDate) return 'Must be before the maturity date';
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="First Rate Change Date *"
                      type="date"
                      fullWidth
                      value={field.value || ''}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: depositDate || undefined, max: maturityDate || undefined }}
                      error={!!error}
                      helperText={error?.message || 'The first date a rate change may take effect'}
                    />
                  )}
                />
              </Grid>
            )}

            {/* T3-098: CUSTOM cadence gate. The fixed frequency list (Monthly … Annual) cannot
                express a non-standard interval like every 18 months or every 7 years. Toggling
                this on reveals a unit + value pair whose resolved month count OVERRIDES the
                selected frequency's months_value; toggling it off clears the pair so a stale
                cadence can never ride along on save.

                A separate gate rather than a "Custom" option inside the frequency dropdown: the
                contract still needs a valid rate_change_frequency_id (an FK the DB requires), and
                only the resolved MONTHS are overridden — a sentinel dropdown value would break
                that FK. Resolution lives in ONE place server-side
                (fn_contract_rate_change_months), so this UI never computes the cadence itself. */}
            {rateType === 'Variable' && rateChangeBasis === RATE_CHANGE_BASIS.SPECIFIED_DATE && (
              <Grid size={{ xs: 12, md: 4 }}>
                <InlineGateToggle
                  label="Custom rate change interval"
                  caption={
                    rateChangeCustomOn
                      ? 'Replaces Rate Change Frequency — no need to select one.'
                      : 'Use a non-standard interval (e.g. every 18 months, every 7 years).'
                  }
                  checked={rateChangeCustomOn}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setValue('rate_change_custom_unit', 'months');
                      setValue('rate_change_custom_value', '');
                      // The custom interval REPLACES the frequency, so clear any prior selection —
                      // leaving it set would submit a value the engine never reads and would show a
                      // stale cadence on the summary screens.
                      setValue('rate_change_frequency_id', '');
                    } else {
                      // Clear BOTH — the DB CHECK chk_rate_change_custom_pair requires
                      // both-or-neither, and the API clears the pair when either is absent.
                      setValue('rate_change_custom_unit', '');
                      setValue('rate_change_custom_value', '');
                    }
                  }}
                />
              </Grid>
            )}

            {rateType === 'Variable' && rateChangeBasis === RATE_CHANGE_BASIS.SPECIFIED_DATE && rateChangeCustomOn && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="rate_change_custom_unit"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Interval Unit *"
                      fullWidth
                      value={field.value || 'months'}
                      helperText="Months or years"
                    >
                      <MenuItem value="months">Months</MenuItem>
                      <MenuItem value="years">Years</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {rateType === 'Variable' && rateChangeBasis === RATE_CHANGE_BASIS.SPECIFIED_DATE && rateChangeCustomOn && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="rate_change_custom_value"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!rateChangeCustomOn) return true;
                      if (value === '' || value == null) return 'Interval is required';
                      const n = Number(value);
                      if (!Number.isInteger(n) || n <= 0) return 'Must be a whole number greater than zero';
                      // Deliberately NO fixed upper cap (African-market terms run long — see
                      // CLAUDE.md); bounded only against THIS contract's own term, and only when
                      // the term is known, so the guard can never fire spuriously.
                      const months = n * (rateChangeCustomUnit === 'years' ? 12 : 1);
                      if (contractTermMonths != null && months > contractTermMonths) {
                        return `Interval (${months} months) cannot exceed the contract term (${contractTermMonths} months)`;
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Every *"
                      type="number"
                      fullWidth
                      value={field.value ?? ''}
                      inputProps={{ min: 1, step: 1 }}
                      error={!!error}
                      helperText={error?.message || rateChangeCadenceHint}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Rate Change Mode - Variable contracts only.
                Default 'bulk' = contract is included in benchmark sweeps.
                'manual' = excluded from sweeps, rate is only changed via per-contract adhoc requests. */}
            {rateType === 'Variable' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_change_mode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Rate Update Mode"
                      fullWidth
                      value={field.value || 'bulk'}
                      helperText={
                        (field.value || 'bulk') === 'manual'
                          ? 'Excluded from benchmark sweeps. Rate only changes via per-contract adhoc requests.'
                          : 'Included in benchmark rate sweeps.'
                      }
                    >
                      <MenuItem value="bulk">Bulk benchmark sweep</MenuItem>
                      <MenuItem value="manual">Manual rate updates only</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {/* Effective Rate Display - for benchmark-linked contracts (Linked/Variable + Floating) */}
            {usesBenchmark && selectedBenchmark && (
              <Grid size={12}>
                <Alert severity="info" icon={false}>
                  <Typography variant="body2">
                    <strong>Effective Rate:</strong>{' '}
                    {selectedBenchmark.interest_rate_type || selectedBenchmark.rate_type} ({benchmarkRateValue.toFixed(2)}%)
                    {parseFloat(rateSpread) !== 0 && (
                      <> {parseFloat(rateSpread) >= 0 ? '+' : ''} {parseFloat(rateSpread).toFixed(2)}%</>
                    )}
                    {' = '}
                    <Chip
                      label={`${effectiveRate.toFixed(2)}%`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Calculation Profile */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="calculation_profile_id"
                control={control}
                rules={{ required: 'Calculation profile is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Calculation Profile *"
                    fullWidth
                    value={field.value || ''}
                    error={!!error}
                    helperText={error?.message || 'Determines interest method, day count, and compounding'}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // If a schedule already exists and the user is switching
                      // to a *different* profile, surface the consequence
                      // before applying. The SP will clear the schedule on the
                      // next save; the dialog gives the user a chance to back
                      // out before they commit.
                      if (hasSchedule
                          && newValue
                          && field.value
                          && String(newValue) !== String(field.value)) {
                        setPendingProfileChange({
                          fromId: field.value,
                          toId: newValue,
                          apply: () => {
                            field.onChange(newValue);
                            handleProfileChange(newValue);
                          },
                        });
                        return;
                      }
                      field.onChange(newValue);
                      handleProfileChange(newValue);
                    }}
                  >
                    <MenuItem value="">
                      <em>Select calculation profile...</em>
                    </MenuItem>
                    {(refData.calculationProfiles || []).filter(p => p.active !== false).map((profile) => (
                      <MenuItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Capitalization Frequency — only for SPC profiles */}
            {selectedProfile?.interest_method === 'Simple Periodic Capitalization' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="capitalization_frequency_id"
                  control={control}
                  // Conditionally required, mirroring the repricing/renewal pattern below.
                  // WHY: the engine THROWS when an SPC contract has no capitalization frequency
                  // (amortizationService.js: "Simple Periodic Capitalization requires a
                  // capitalization frequency with a defined month value"). Without this rule that
                  // throw arrives at Generate Schedule — AFTER the contract has already been
                  // saved — leaving a persisted Draft that cannot produce a schedule and so
                  // cannot be submitted. The auto-fill in handleProfileChange is not sufficient:
                  // it is bypassed by re-selecting the blank option, by reference data with no
                  // row named 'Annual'/'Annually', and by edit-load (where it never runs).
                  rules={{
                    validate: (value) => {
                      if (selectedProfile?.interest_method !== 'Simple Periodic Capitalization') return true;
                      if (!value) return 'Capitalization frequency is required for this calculation profile';
                      const selected = (refData.frequencies || []).find(
                        (f) => f.id === parseInt(value, 10)
                      );
                      if (!selected) return true;
                      // Adhoc (998) / At Maturity (999) carry no usable month value: the engine
                      // rejects both, and capitalizing once at maturity is just simple interest.
                      if (selected.months_value == null
                          || selected.months_value <= 0
                          || selected.months_value >= 998) {
                        return `${selected.frequency} cannot be used for capitalization — choose a periodic frequency (Monthly, Quarterly, Bi-Annual, Annual)`;
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Capitalization Frequency *"
                      fullWidth
                      value={field.value || ''}
                      error={!!error}
                      helperText={error?.message || 'How often accrued interest is added to principal — this sets the compounding interval'}
                    >
                      <MenuItem value="">
                        <em>Select frequency...</em>
                      </MenuItem>
                      {/* Only periodic cadences are valid for capitalization. Exclude
                          the Adhoc (998) and At Maturity (999) sentinels: there is no
                          mechanism to set ad-hoc capitalization dates, and capitalizing
                          once at maturity is identical to plain simple interest. Filter
                          on months_value so it is independent of localized names. */}
                      {(refData.frequencies || [])
                        .filter(f => f.active !== false && f.months_value != null && f.months_value > 0 && f.months_value < 998)
                        .map((freq) => (
                          <MenuItem key={freq.id} value={freq.id}>
                            {freq.frequency}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {/* Interest Rate */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="interest_rate"
                control={control}
                rules={{
                  required: 'Interest rate is required',
                  validate: (value) => {
                    const num = parseFloat(value);
                    if (isNaN(num)) return 'Must be a valid number';
                    if (num < 0) return 'Cannot be negative';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Interest Rate (%) *"
                    type="number"
                    fullWidth
                    // When a benchmark is selected, the rate is driven by
                    // benchmark + spread (kept in sync by the effect above) and
                    // must not be hand-edited, or the stored rate could diverge
                    // from the displayed Effective Rate.
                    disabled={!!selectedBenchmark}
                    inputProps={{ step: 0.01, min: 0 }}
                    InputLabelProps={selectedBenchmark ? { shrink: true } : undefined}
                    error={!!error}
                    helperText={
                      error?.message ||
                      (selectedBenchmark ? 'Calculated from benchmark + spread' : undefined)
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Profile formula display */}
          {selectedProfile && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedProfile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedProfile.description}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#fff', p: 1, borderRadius: 1 }}>
                {selectedProfile.formula_display}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={selectedProfile.interest_method} size="small" variant="outlined" />
                <Chip label={selectedProfile.day_count_convention} size="small" variant="outlined" />
                {/* compounding_frequency is INERT for Simple Periodic Capitalization — the engine
                    never reads it on that path (calculateInterest routes only Compound and
                    Compound Linear to the compound formula; SPC falls through to simple and gets
                    its compounding from the stepped spcPrincipal). The interval that actually
                    drives an SPC schedule is the CONTRACT's Capitalization Frequency field below.
                    Showing "Compounding: 1x/year" beside that field put two competing answers on
                    screen, one of them meaningless — so SPC is excluded here and told the truth
                    in the caption instead. */}
                {selectedProfile.compounding_frequency
                  && selectedProfile.interest_method !== 'Simple'
                  && selectedProfile.interest_method !== 'Simple Periodic Capitalization' && (
                  <Chip label={`Compounding: ${selectedProfile.compounding_frequency}x/year`} size="small" variant="outlined" />
                )}
                {selectedProfile.interest_method === 'Simple Periodic Capitalization' && (
                  <Chip
                    label="Compounding interval: set by Capitalization Frequency below"
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                )}
              </Box>
            </Paper>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Payment Frequencies
          </Typography>

          {/* T064: Switch | Interest | Capital — three equal columns. Top-aligned
              so the switch tracks the field labels, not their centres. The switch
              column wraps in a flex so the control sits at the same baseline as
              the TextField inputs (which have label space above). */}
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="combine_payment_rows"
                control={control}
                render={({ field }) => {
                  // T076: Annuity profiles require combine_payment_rows = true.
                  // Force on and lock the toggle so the user can see the state but can't change it.
                  const isAnnuity = selectedProfile?.interest_method === 'Annuity';
                  const caption = isAnnuity
                    ? 'Required by Annuity profile.'
                    : (isInterestAtMaturity ? 'Not applicable — interest is paid at maturity.' : null);
                  return (
                    <InlineGateToggle
                      label="Combined payment"
                      caption={caption}
                      checked={field.value === true && !isInterestAtMaturity}
                      disabled={isAnnuity || isInterestAtMaturity}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        field.onChange(checked);
                        if (checked && interestFreqId) {
                          setValue('capital_paid_frequency_id', interestFreqId);
                        } else if (!checked) {
                          setValue('equal_installment', false);
                        }
                      }}
                    />
                  );
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="interest_paid_frequency_id"
                control={control}
                rules={{ required: 'Interest payment frequency is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Interest Payment Frequency *"
                    fullWidth
                    error={!!error}
                    helperText={error?.message || ' '}
                    onChange={(e) => {
                      field.onChange(e);
                      // If the newly selected interest frequency is "At Maturity" (999),
                      // there is no periodic interest stream — force-clear the toggles
                      // (the server-side guard in sp_contract_create/update is the backstop).
                      const selected = (refData.frequencies || []).find((f) => f.id === e.target.value);
                      if (selected?.months_value === 999) {
                        setValue('combine_payment_rows', false);
                        setValue('equal_installment', false);
                      } else if (combinePaymentRows) {
                        setValue('capital_paid_frequency_id', e.target.value);
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select frequency...</em>
                    </MenuItem>
                    {/* Adhoc (998) is not a user-selectable interest cadence in the wizard. */}
                    {refData.frequencies
                      .filter((freq) => freq.months_value !== 998)
                      .map((freq) => (
                        <MenuItem key={freq.id} value={freq.id}>
                          {freq.months_value === 999 ? 'At Maturity (single payment)' : freq.frequency}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="capital_paid_frequency_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Capital Payment Frequency"
                    fullWidth
                    disabled={combinePaymentRows === true}
                    error={!!error}
                    // displayEmpty renders "At Maturity (default)" even when the value is
                    // empty, so force the label to float — otherwise it sits unshrunk and
                    // overlaps the rendered value.
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) return 'At Maturity (default)';
                        const f = refData.frequencies.find((x) => x.id === selected);
                        return f ? f.frequency : '';
                      },
                    }}
                    helperText={
                      combinePaymentRows
                        ? 'Locked to interest payment frequency'
                        : (error?.message || 'Optional: Defaults to "At Maturity" if not specified')
                    }
                  >
                    {/* The empty option below IS "At Maturity" (persists capital_paid_frequency_id = NULL,
                        the canonical bullet state). Filter out the At-Maturity (999) and Adhoc (998)
                        sentinel rows so the dropdown doesn't show a confusing duplicate / invalid option. */}
                    <MenuItem value="">At Maturity (default)</MenuItem>
                    {refData.frequencies
                      .filter((freq) => freq.months_value !== 999 && freq.months_value !== 998)
                      .map((freq) => (
                        <MenuItem key={freq.id} value={freq.id}>
                          {freq.frequency}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          {/* Options — the standalone contract booleans grouped in one place.
              Equal-installment is ALWAYS rendered (greyed-out rather than hidden):
                - Annuity profile: force-on + disabled (intrinsic to the profile).
                - Simple profile: enabled — the user chooses.
                - Compound / Compound Linear / SPC: force-off + disabled + an alert
                  explaining the engine supports Simple/Annuity only.
              It is no longer gated behind combinePaymentRows: equal_installment is a
              valid, independent choice (the engine applies combine as a separate
              post-step), so a Simple contract can use it without combining rows. */}
          {(() => {
            const profileMethod = selectedProfile?.interest_method;
            const isAnnuity = profileMethod === 'Annuity';
            const isSimple = !profileMethod || profileMethod === 'Simple';
            const eiCaption = isAnnuity
              ? 'On by default for Annuity profiles. The engine solves a constant total payment using the periodic rate r/N (N = compounding frequency), accrues interest on actual calendar days, and re-solves the payment at each rate change.'
              : 'Engine solves for a constant total payment that amortises the loan to zero by maturity. The payment is re-solved automatically at each rate change.';
            return (
              <>
                <ToggleGroup title="Options" sx={{ mt: 2 }}>
                  <Controller
                    name="equal_installment"
                    control={control}
                    render={({ field }) => (
                      <LabeledSwitch
                        label="Use equal-installment repayment"
                        caption={eiCaption}
                        checked={field.value === true && !isInterestAtMaturity}
                        disabled={isAnnuity || !isSimple || isInterestAtMaturity}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                  <Controller
                    name="exclude_leap_day"
                    control={control}
                    render={({ field }) => (
                      <LabeledSwitch
                        label="Exclude leap day"
                        caption="February 29 is excluded from interest day counts (February always has 28 days)."
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                </ToggleGroup>
                {!isSimple && !isAnnuity && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Equal-installment supports Simple-interest and Annuity profiles only. Select a Simple profile (e.g. Simple Actual/365) or the Equal Instalment Annuity (Monthly) profile to enable.
                  </Alert>
                )}
                {isInterestAtMaturity && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Not applicable when interest is paid at maturity — there is no periodic interest stream to combine or levelise.
                  </Alert>
                )}
              </>
            );
          })()}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Note: Capital payment frequency cannot be more frequent than interest payment frequency.
          </Typography>
        </CardContent>
      </Card>

      {rateType === 'Floating' && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Re-pricing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set the cadence and effective date for re-pricing this Floating-rate contract. Upcoming re-pricing dates will appear on the Dashboard's Upcoming Events list.
            </Typography>

            {/* Toggle inline with its Frequency + Date fields (toggle | frequency | date),
                mirroring the Combined | Interest | Capital frequencies row above. */}
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="eligible_for_repricing"
                  control={control}
                  render={({ field }) => (
                    <InlineGateToggle
                      label="Eligible for re-pricing"
                      caption="Upcoming re-pricing dates appear on the Dashboard's Upcoming Events list."
                      checked={!!field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          setValue('repricing_frequency_id', '');
                          setValue('first_repricing_date', '');
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              {eligibleForRepricing && (
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="repricing_frequency_id"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!eligibleForRepricing) return true;
                        if (!value) return 'Re-pricing frequency is required';
                        const selected = (refData.frequencies || []).find(
                          (f) => f.id === parseInt(value, 10)
                        );
                        if (!selected) return true;
                        if (selected.frequency && selected.frequency.toLowerCase() === 'adhoc') {
                          return 'Adhoc cannot be used for re-pricing';
                        }
                        if (
                          contractTermMonths != null &&
                          selected.months_value &&
                          selected.months_value > contractTermMonths
                        ) {
                          return 'Re-pricing frequency cannot exceed the contract term';
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Re-pricing Frequency *"
                        fullWidth
                        error={!!error}
                        helperText={error?.message || 'How often the rate should be reviewed'}
                      >
                        <MenuItem value="">
                          <em>Select frequency...</em>
                        </MenuItem>
                        {repricingFrequencyOptions.map((freq) => (
                          <MenuItem key={freq.id} value={freq.id}>
                            {freq.frequency}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              )}

              {eligibleForRepricing && (
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="first_repricing_date"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!eligibleForRepricing) return true;
                        if (!value) return 'First re-pricing date is required';
                        if (depositDate && value <= depositDate) {
                          return 'Must be after the deposit date';
                        }
                        if (maturityDate && value >= maturityDate) {
                          return 'Must be before the maturity date';
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="First Re-pricing Date *"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: depositDate || undefined,
                          max: maturityDate || undefined,
                        }}
                        error={!!error}
                        helperText={error?.message || 'When the first re-pricing event takes effect'}
                      />
                    )}
                  />
                </Grid>
              )}

            </Grid>

            {/* T105: Floating-only manual schedule-editing gate. Lets an admin
                later edit interest-accrual amounts on the schedule (e.g. to
                match an investor statement). Must be enabled BEFORE approval —
                it cannot be turned on afterwards. */}
            <ToggleGroup title="Schedule editing" sx={{ mt: 2 }}>
              <Controller
                name="allow_schedule_editing"
                control={control}
                render={({ field }) => (
                  <LabeledSwitch
                    label="Allow manual schedule editing"
                    caption="Enables manual editing of interest-accrual amounts on the amortization schedule for this Floating-rate contract. Must be enabled before the contract is approved — it cannot be changed afterwards."
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </ToggleGroup>
          </CardContent>
        </Card>
      )}

      {/* T127: Renewal opt-out point. Applies to ALL rate types (unlike re-pricing, which is
          Floating-only). A renewal date is a point where the contract can either terminate or
          continue to maturity. Modelled like re-pricing: flag + recurring frequency + first date.
          Upcoming renewal dates surface on the Dashboard alongside re-pricing events. */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Renewal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mark this contract as eligible for renewal and set the opt-out point(s) — dates where the contract can either terminate or continue to maturity.
          </Typography>

          {/* Toggle inline with its Frequency + Date fields (toggle | frequency | date),
              mirroring the Combined | Interest | Capital frequencies row above. */}
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="eligible_for_renewal"
                control={control}
                render={({ field }) => (
                  <InlineGateToggle
                    label="Eligible for renewal"
                    caption="A renewal date is a point where the contract can terminate or continue to maturity. Upcoming renewals show on the Dashboard alongside re-pricing events."
                    checked={!!field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (!e.target.checked) {
                        setValue('renewal_frequency_id', '');
                        setValue('first_renewal_date', '');
                      }
                    }}
                  />
                )}
              />
            </Grid>
            {eligibleForRenewal && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="renewal_frequency_id"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!eligibleForRenewal) return true;
                      if (!value) return 'Renewal frequency is required';
                      const selected = (refData.frequencies || []).find(
                        (f) => f.id === parseInt(value, 10)
                      );
                      if (!selected) return true;
                      if (selected.frequency && selected.frequency.toLowerCase() === 'adhoc') {
                        return 'Adhoc cannot be used for renewal';
                      }
                      if (
                        contractTermMonths != null &&
                        selected.months_value &&
                        selected.months_value > contractTermMonths
                      ) {
                        return 'Renewal frequency cannot exceed the contract term';
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Renewal Frequency *"
                      fullWidth
                      error={!!error}
                      helperText={error?.message || 'How often a renewal opt-out point occurs'}
                    >
                      <MenuItem value="">
                        <em>Select frequency...</em>
                      </MenuItem>
                      {repricingFrequencyOptions.map((freq) => (
                        <MenuItem key={freq.id} value={freq.id}>
                          {freq.frequency}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {eligibleForRenewal && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="first_renewal_date"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!eligibleForRenewal) return true;
                      if (!value) return 'First renewal date is required';
                      if (depositDate && value <= depositDate) {
                        return 'Must be after the deposit date';
                      }
                      if (maturityDate && value >= maturityDate) {
                        return 'Must be before the maturity date';
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="First Renewal Date *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: depositDate || undefined,
                        max: maturityDate || undefined,
                      }}
                      error={!!error}
                      helperText={error?.message || 'The first opt-out / continuation point'}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* T077: profile-change confirm dialog. Triggered when the user picks a
          different calculation profile while a schedule is already generated.
          On confirm, the new profile is applied and the schedule will be
          cleared on the next save (SP-side enforcement in sp_contract_update). */}
      <Dialog
        open={pendingProfileChange !== null}
        onClose={() => setPendingProfileChange(null)}
      >
        <DialogTitle>Change calculation profile?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This contract already has a generated schedule. Changing the
            calculation profile from <strong>{
              (refData.calculationProfiles || []).find(p => p.id === parseInt(pendingProfileChange?.fromId, 10))?.name || '—'
            }</strong> to <strong>{
              (refData.calculationProfiles || []).find(p => p.id === parseInt(pendingProfileChange?.toId, 10))?.name || '—'
            }</strong> will clear the current schedule. You will need to
            regenerate it before submitting the contract for approval.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingProfileChange(null)}>Cancel</Button>
          <Button
            onClick={() => {
              pendingProfileChange?.apply?.();
              // Optimistically clear the local schedule state. The SP will
              // delete the persisted rows on the next save (sp_contract_update
              // detects the calculation_profile_id change and clears).
              setHasSchedule(false);
              setSchedulePreview(null);
              setPendingProfileChange(null);
            }}
            color="primary"
            variant="contained"
          >
            Change profile
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InterestPaymentsStep;
